import React, { useMemo, useState } from "react";

/**
 * CareLogix™ ORACLE — Instruments Screen (Phase 22).
 *
 * Three tabs: Favorites / Popular / Forex.
 * Each row shows bid · spread · ask · % change · pips.
 *
 * Props:
 *   data: { favorites, popular, forex } (each = array of rows)
 *   onPick(instrument)
 */
export default function OandaInstrumentsScreen({ data, onPick } = {}) {
  const [tab, setTab] = useState("popular");
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    if (!data) return [];
    const list = data[tab] || [];
    const term = q.trim().toUpperCase();
    if (!term) return list;
    return list.filter((r) => (r.pair || r.instrument || "").toUpperCase().includes(term));
  }, [data, tab, q]);

  return (
    <section className="fx-inst" data-testid="fx-instruments-screen">
      <div className="fx-inst__search">
        <input
          type="text"
          placeholder="Search…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search instruments"
          data-testid="fx-instruments-search"
        />
      </div>
      <div className="fx-inst__tabs" role="tablist">
        {["favorites", "popular", "forex"].map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className={`fx-inst__tab ${tab === t ? "is-active" : ""}`}
            onClick={() => setTab(t)}
            data-testid={`fx-instruments-tab-${t}`}
          >
            {t.toUpperCase()}
          </button>
        ))}
      </div>
      <div className="fx-inst__head">
        <span className="fx-inst__head-pair">PAIR</span>
        <span>BID</span>
        <span>SPREAD</span>
        <span>ASK</span>
        <span>% CHG</span>
      </div>
      <ul className="fx-inst__list">
        {rows.map((r) => {
          const up = (r.pct_change_24h ?? 0) >= 0;
          return (
            <li key={r.instrument}>
              <button
                type="button"
                className="fx-inst__row"
                onClick={() => onPick && onPick(r.instrument)}
                aria-label={`Open ${r.pair} chart`}
                data-testid={`fx-instruments-row-${r.instrument}`}
              >
                <span className="fx-inst__pair">{r.pair}</span>
                <span className="fx-inst__bid">{r.bid}</span>
                <span className="fx-inst__spread">{r.spread_pips}</span>
                <span className="fx-inst__ask">{r.ask}</span>
                <span className={`fx-inst__pct ${up ? "is-up" : "is-down"}`}>
                  {up ? "▲" : "▼"} {Math.abs(r.pct_change_24h ?? 0).toFixed(3)}%
                  <small> {Math.abs(r.change_pips_24h ?? 0).toFixed(1)} pips</small>
                </span>
              </button>
            </li>
          );
        })}
        {rows.length === 0 && <li className="fx-inst__empty">No instruments match.</li>}
      </ul>
    </section>
  );
}
